import type { CmsSellerProfile } from '@/helpers/api/fetchPayload'

export function lexicalHasContent(data: unknown): boolean {
  if (!data || typeof data !== 'object' || !('root' in data)) return false
  const root = (data as { root?: { children?: unknown[] } }).root
  return Array.isArray(root?.children) && root.children.length > 0
}

export function hasCmsAboutBody(profile: CmsSellerProfile): boolean {
  if (lexicalHasContent(profile.aboutIntro)) return true
  const sections = profile.aboutSections
  if (!Array.isArray(sections)) return false
  return sections.some(
    (s) =>
      (typeof s.sectionTitle === 'string' &&
        s.sectionTitle.trim().length > 0) ||
      lexicalHasContent(s.body)
  )
}

/**
 * The slice of a seller's CMS profile safe to hand to the sign-in wall for a signed-out
 * visitor: only the fields `SignInWarning`/`SignInAbout` read. A `'use client'` component
 * serializes its ENTIRE props tree into the page's RSC payload for hydration, regardless of
 * which fields it actually renders, so passing the full profile here sends every hidden tab's
 * CMS body (conditions, shipping, device unlocking, terms) to a browser that hasn't signed in
 * or accepted terms yet. Built as an explicit allowlist, not a denylist of the sensitive
 * fields, so a new `CmsSellerProfile` field defaults to withheld instead of leaking until
 * someone remembers to exclude it.
 */
export function cmsSellerProfileForSignInWall(
  profile: CmsSellerProfile | null | undefined
): CmsSellerProfile | null {
  if (!profile) return null
  return {
    id: profile.id,
    sellerName: profile.sellerName,
    sellerSlug: profile.sellerSlug,
    storeFrontId: profile.storeFrontId,
    aboutOnSignInEnabled: profile.aboutOnSignInEnabled,
    signInAboutStyle: profile.signInAboutStyle,
    signInAbout: profile.signInAbout,
    aboutIntro: profile.aboutIntro,
    aboutSections: profile.aboutSections,
  }
}
